// ** Redux
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// ** Third party
import i18n from "config/i18n";
import cookie from "js-cookie";

// ** Helpers
import { createApiCall } from "helpers/createApiCall";

// ** Configuration
import config from "configuration";

// ** Actions
import { login } from "./auth/actions/login.action";

// ** Utils & Helpers
import { overflowChanger } from "helpers/overflowChanger";
import { redirectToLanguageSubdomain } from "helpers/languageSubdomainHandler";

// Globalny Slice zawierający statystyki strony, user-agenta użytkownika, wybrany język, dark-mode itp.
// Bez informacji na temat zalogowanego użytkownika - je umieszczamy w global-user.slice.js

// ############################################################

export const getGlobalStatistics = createAsyncThunk(
	"globalCommon/getGlobalStatistics",
	createApiCall(async (_, client) => {
		const response = await client.get(`/datezone/statistics/main`);
		return { result: response.data };
	}),
);

export const setLanguageAsync = createAsyncThunk(
	"globalCommon/setLanguage",
	createApiCall(async ({ language, setCookie, withRedirect = true }) => {
		const finalLanguage = language ? language.slice(0, 2) : "pl";

		if (setCookie && typeof window !== "undefined") {
			cookie.set(
				config.language.cookieName,
				finalLanguage,
				config.language.cookieOptionsJsCookie,
			);
		}

		if (withRedirect) {
			redirectToLanguageSubdomain(finalLanguage);
		}

		return { language: finalLanguage };
	}),
);

// ############################################################

// Initial state
const initialState = {
	userAgent: {
		ua: "",
		browser: {
			name: "",
			version: "",
			major: "",
		},
		engine: {
			name: "",
			version: "",
		},
		os: {
			name: "",
			version: "",
		},
		device: {
			vendor: "",
			model: "",
			type: "",
		},
		cpu: {
			architecture: "",
		},
	},
	statistics: {
		usersOnline: 0,
		usersTotal: 0,
		mediaTotal: 0,
	},
	subdomain: null,
	darkMode: {
		isDarkMode: false,
		isChangedByUser: false,
		isSetByBrowser: false,
	},
	language: "pl",
	isClient: false,
	isAuth: false,
	oauthActive: false,
	cookieConsent: {
		showBanner: false,
		showCustomization: false,
		value: "default",
		isInitialized: false,
		settings: {
			ad_storage: false,
			ad_user_data: false,
			ad_personalization: false,
			analytics_storage: false,
			functionality_storage: true,
			security_storage: true,
		},
	},
	oAuth: {
		provider: null,
		email: null,
		token: null,
		active: false,
		loading: false,
		reset: false,
	},
	authModal: {
		version: "onlyDark",
		hasSetVersion: false,
		isOpen: false,
	},
	registrationVerifyEmailModal: {
		isOpen: false,
		isRedirectedFromLanding: false,
	},
	registrationVerifyPhoneModal: {
		isOpen: false,
		login: null,
		id: null,
		hash: null,
	},
	scriptsLoaded: {
		tj: false,
	},
};

// ############################################################

// Slice
const globalCommonSlice = createSlice({
	name: "global/common",
	initialState,
	reducers: {
		// ** User Agent
		setUserAgent: (state, action) => {
			state.userAgent.ua = action.payload.ua;
			state.userAgent.browser = action.payload.browser;
			state.userAgent.engine = action.payload.engine;
			state.userAgent.os = action.payload.os;
			state.userAgent.device = action.payload.device;
			state.userAgent.cpu = action.payload.cpu;
		},
		// ** Global Statistics
		setGlobalStatistics(state, action) {
			state.statistics = {
				usersOnline: action.payload.usersOnline,
				usersTotal: action.payload.usersTotal,
				mediaTotal: action.payload.mediaTotal,
			};
		},
		// ** Global Subdomain
		setGlobalSubdomain(state, action) {
			state.subdomain = action.payload.subdomain;
		},
		// ** Dark Mode
		setIsInitialized(state) {
			state.darkMode.isSetByBrowser = true;
		},
		setDarkModeFromBrowser(state, action) {
			state.darkMode.isDarkMode = action.payload.isDarkMode;
			state.darkMode.isSetByBrowser = true;
		},
		setDarkModeByUser(state, action) {
			state.darkMode.isDarkMode = action.payload.isDarkMode;
			state.darkMode.isChangedByUser =
				action.payload.isChangedByUser || true;
			state.darkMode.isSetByBrowser = true;
		},
		// ** Language
		setLanguageManually(state, action) {
			state.language = action.payload.language;
		},
		// ** Is Client
		setIsClient(state, action) {
			state.isClient = action.payload;
		},
		setCookieConsentBannerVisibility: (state, action) => {
			state.cookieConsent.showBanner = action.payload.showBanner;
			state.cookieConsent.showCustomization =
				action.payload.showCustomization;
		},
		// ** Is Auth
		setIsAuth(state, action) {
			state.isAuth = action.payload;
		},
		// ** OAuth
		setActiveOauth(state, action) {
			state.oauthActive = action.payload;
		},
		setOAuth(state, action) {
			state.oAuth = action.payload;
		},
		logout(state, action) {
			state.isAuth = false;

			if (
				action?.payload?.login === "rzurzu" ||
				action?.payload?.login === "sftester" ||
				action?.payload?.login === "sftester2" ||
				action?.payload?.login === "lovemoschino1" ||
				action?.payload?.login === "testDamian" ||
				action?.payload?.login === "testdamian" ||
				action?.payload?.login === "reeecz3" ||
				action?.payload?.login === "ulaux"
			) {
				const data = {
					route: window?.location?.href ?? "FAILED_TO_GET_ROUTE",
					from: `logoutAction -> ${action.payload.login}`,
					initiator: action.payload.initiator,
					timestamp: new Date().toLocaleString(),
					userAgent: navigator.userAgent,
					token: action?.payload?.token,
				};

				if (action?.payload?.api) {
					action.payload.api
						.post("/system/react-error", data)
						.catch((err) => {
							console.log("Failed to send logout log:", err);

							try {
								if (typeof localStorage !== "undefined") {
									const existing = JSON.parse(
										localStorage.getItem(
											"unsentLogoutLogs",
										) || "[]",
									);
									existing.push(data);
									localStorage.setItem(
										"unsentLogoutLogs",
										JSON.stringify(existing),
									);
									console.log(
										"Logout log saved to localStorage",
									);
								}
							} catch (storageError) {
								console.log(
									"Unable to write to localStorage",
									storageError,
								);
							}
						});
				}
			}
		},

		// ** Consent Mode
		setConsentModeValues(state, action) {
			state.cookieConsent.value = action.payload?.value || "default";
			state.cookieConsent.isInitialized =
				action.payload?.isInitialized || false;
			state.cookieConsent.settings.ad_storage =
				action.payload?.settings?.ad_storage ||
				initialState.cookieConsent.settings.ad_storage;
			state.cookieConsent.settings.ad_user_data =
				action.payload?.settings?.ad_user_data ||
				initialState.cookieConsent.settings.ad_user_data;
			state.cookieConsent.settings.ad_personalization =
				action.payload?.settings?.ad_personalization ||
				initialState.cookieConsent.settings.ad_personalization;
			state.cookieConsent.settings.analytics_storage =
				action.payload?.settings?.analytics_storage ||
				initialState.cookieConsent.settings.analytics_storage;
		},
		// ** Auth Modal
		openAuthModal(state) {
			overflowChanger({ conditionName: "ifIsNotHidden" });

			state.authModal.isOpen = true;
		},
		closeAuthModal(state) {
			overflowChanger({ conditionName: "ifIsHidden" });

			state.authModal.isOpen = false;
		},
		// ** Registration Verify Email Modal
		openRegistrationVerifyEmailModal(state) {
			state.registrationVerifyEmailModal.isOpen = true;
		},
		closeRegistrationVerifyEmailModal(state) {
			state.registrationVerifyEmailModal.isOpen = false;
		},
		setIsRedirectedFromLanding(state, action) {
			state.registrationVerifyEmailModal.isRedirectedFromLanding =
				action.payload;
		},
		// ** Registration Verify Phone Modal
		openRegistrationVerifyPhoneModal(state) {
			state.registrationVerifyPhoneModal.isOpen = true;
		},
		closeRegistrationVerifyPhoneModal(state) {
			state.registrationVerifyPhoneModal.isOpen = false;
		},
		setRegistrationVerifyPhoneModalParams(state, action) {
			state.registrationVerifyPhoneModal.login = action.payload.login;
			state.registrationVerifyPhoneModal.id = action.payload.id;
			state.registrationVerifyPhoneModal.hash = action.payload.hash;
		},
		// ** Scripts Loaded
		setScriptsLoaded(state, action) {
			state.scriptsLoaded[action.payload.name] = action.payload.isLoaded;
		},
	},
	extraReducers: (builder) => {
		builder
			// Get global statistics
			.addCase(getGlobalStatistics.fulfilled, (state, action) => {
				state.statistics = {
					...state.statistics,
					usersTotal: action.payload.users,
					mediaTotal: action.payload.media,
				};
			})
			// Set language
			.addCase(setLanguageAsync.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(setLanguageAsync.fulfilled, (state, action) => {
				state.isLoading = false;
				state.language = action.payload.language;
				// Cookie jest teraz ustawiany w thunk action - brak efektów ubocznych w reducerze
			})
			.addCase(setLanguageAsync.rejected, (state, action) => {
				state.isLoading = false;
				state.error =
					action.payload || "Wystąpił błąd podczas zmiany języka";
			})
			.addCase(login.fulfilled, (state, action) => {
				state.isAuth = action.payload.isTokenExists;
			});
	},
});

// ############################################################

export default globalCommonSlice.reducer;

// ** Actions
export const {
	setUserAgent,
	setGlobalStatistics,
	setGlobalSubdomain,
	setDarkModeFromBrowser,
	setDarkModeByUser,
	setLanguageManually,
	setIsClient,
	setCookieConsentBannerVisibility,
	setIsAuth,
	setActiveOauth,
	setOAuth,
	setConsentModeValues,
	openAuthModal,
	closeAuthModal,
	openRegistrationVerifyEmailModal,
	closeRegistrationVerifyEmailModal,
	openRegistrationVerifyPhoneModal,
	closeRegistrationVerifyPhoneModal,
	setRegistrationVerifyPhoneModalParams,
	setIsRedirectedFromLanding,
	setScriptsLoaded,
	setAuthModalVersion,
	logout,
} = globalCommonSlice.actions;

// ############################################################
